/**
 * Sdílené ovládací prvky. Velké dotykové plochy – ovládá se to
 * u úlu, často v rukavicích.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { back, go } from './hooks';

export function Screen(props: {
  title: string;
  subtitle?: string;
  /** Kde v aplikaci jsem: [popisek, cesta] – ťuknutím se tam vrátí. */
  crumbs?: [string, string][];
  backTo?: string | false;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <header className="bar">
        {props.backTo !== false && (
          <button className="bar-back" aria-label="Zpět" onClick={() => back(props.backTo || '')}>
            ‹
          </button>
        )}
        <div className="bar-title">
          {props.crumbs && props.crumbs.length > 0 && (
            <nav className="crumbs" aria-label="Kde jsem">
              {props.crumbs.map(([label, to], i) => (
                <span key={to + i}>
                  {i > 0 && <span className="crumb-sep">›</span>}
                  <a href={'#/' + to} onClick={(e) => (e.preventDefault(), go(to))}>
                    {label}
                  </a>
                </span>
              ))}
            </nav>
          )}
          <h1>{props.title}</h1>
          {props.subtitle && <p>{props.subtitle}</p>}
        </div>
        {props.actions && <div className="bar-actions">{props.actions}</div>}
      </header>
      <main className="content">{props.children}</main>
    </>
  );
}

export function Link(props: { to: string; className?: string; children: ReactNode; ariaLabel?: string }) {
  return (
    <a
      href={'#/' + props.to}
      className={props.className}
      aria-label={props.ariaLabel}
      onClick={(e) => {
        e.preventDefault();
        go(props.to);
      }}
    >
      {props.children}
    </a>
  );
}

export function Section(props: { title?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="section">
      {(props.title || props.aside) && (
        <div className="section-head">
          {props.title && <h2>{props.title}</h2>}
          {props.aside}
        </div>
      )}
      {props.children}
    </section>
  );
}

export function Field(props: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={props.htmlFor}>
        {props.label}
      </label>
      {props.children}
      {props.hint && <div className="field-hint">{props.hint}</div>}
    </div>
  );
}

/** Výběr jedné možnosti tlačítky (rychlejší než rozbalovací seznam). */
export function Chips<T extends string | number>(props: {
  options: [T, string][];
  value: T | undefined | null;
  onChange: (v: T | undefined) => void;
  allowNone?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div className="chips" role="radiogroup" aria-label={props.ariaLabel}>
      {props.options.map(([v, label]) => {
        const on = props.value === v;
        return (
          <button
            key={String(v)}
            type="button"
            role="radio"
            aria-checked={on}
            className={'chip' + (on ? ' on' : '')}
            onClick={() => props.onChange(on && props.allowNone ? undefined : v)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Ano / Ne / nevyplněno. */
export function YesNo(props: { value: boolean | undefined; onChange: (v: boolean | undefined) => void; label: string }) {
  return (
    <div className="yesno">
      <span>{props.label}</span>
      <Chips
        ariaLabel={props.label}
        allowNone
        options={[
          ['y', 'Ano'],
          ['n', 'Ne'],
        ]}
        value={props.value === undefined ? undefined : props.value ? 'y' : 'n'}
        onChange={(v) => props.onChange(v === undefined ? undefined : v === 'y')}
      />
    </div>
  );
}

/** Číslo s tlačítky − / +. Přijímá desetinnou čárku i tečku. */
export function Stepper(props: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  step?: number;
  min?: number;
  max?: number;
  unit?: string;
  ariaLabel?: string;
}) {
  const step = props.step ?? 1;
  const min = props.min ?? 0;
  const show = (n: number | undefined) => (n == null ? '' : String(n).replace('.', ','));
  // vlastní text, aby šlo napsat „2,“ a pokračovat „2,5“
  const [text, setText] = useState(show(props.value));
  useEffect(() => {
    const parsed = parseNum(text);
    if (parsed !== props.value) setText(show(props.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);
  const clamp = (n: number) => Math.min(props.max ?? Infinity, Math.max(min, Math.round(n * 100) / 100));
  const bump = (dir: 1 | -1) => {
    // z prázdného pole: + dá první krok (nebo minimum), − dá minimum
    const next = props.value == null ? (dir > 0 && min <= 0 ? clamp(step) : min) : clamp(props.value + dir * step);
    setText(show(next));
    props.onChange(next);
  };
  return (
    <div className="stepper">
      <button type="button" aria-label="Méně" onClick={() => bump(-1)}>
        −
      </button>
      <input
        inputMode="decimal"
        aria-label={props.ariaLabel}
        value={text}
        placeholder="–"
        onChange={(e) => {
          const t = e.target.value.replace(/[^0-9.,-]/g, '');
          setText(t);
          props.onChange(parseNum(t));
        }}
        onBlur={() => setText(show(props.value))}
      />
      {props.unit && <span className="stepper-unit">{props.unit}</span>}
      <button type="button" aria-label="Více" onClick={() => bump(1)}>
        +
      </button>
    </div>
  );
}

function parseNum(t: string): number | undefined {
  const v = t.replace(',', '.').trim();
  if (v === '' || v === '-' || v === '.') return undefined;
  const n = Number(v.endsWith('.') ? v.slice(0, -1) : v);
  return isNaN(n) ? undefined : n;
}

/** Pole pro datum, které nejde omylem vymazat (prázdná hodnota se ignoruje). */
export function DateInput(props: { id?: string; value: string | undefined; onChange: (v: string) => void; min?: string; max?: string; optional?: boolean }) {
  return (
    <input
      id={props.id}
      type="date"
      required={!props.optional}
      value={props.value ?? ''}
      min={props.min}
      max={props.max}
      onChange={(e) => {
        const v = e.target.value;
        if (v || props.optional) props.onChange(v);
      }}
    />
  );
}

export function Rating(props: { value: number | undefined; onChange: (v: number | undefined) => void; ariaLabel: string }) {
  return (
    <Chips
      ariaLabel={props.ariaLabel}
      allowNone
      options={[1, 2, 3, 4, 5].map((n) => [n, String(n)] as [number, string])}
      value={props.value}
      onChange={props.onChange}
    />
  );
}

export function TextInput(props: {
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  type?: string;
  multiline?: boolean;
  autoFocus?: boolean;
  /** Bez automatických velkých písmen a opravy textu (hesla, kódy). */
  plain?: boolean;
}) {
  if (props.multiline)
    return (
      <textarea
        id={props.id}
        rows={3}
        value={props.value ?? ''}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
      />
    );
  return (
    <input
      id={props.id}
      type={props.type ?? 'text'}
      value={props.value ?? ''}
      placeholder={props.placeholder}
      autoFocus={props.autoFocus}
      {...(props.plain ? { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false, autoComplete: 'off' } : {})}
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
}

export function Empty(props: { children: ReactNode }) {
  return <div className="empty">{props.children}</div>;
}

export function Notice(props: { tone?: 'warn' | 'info' | 'ok'; children: ReactNode }) {
  return <div className={'notice ' + (props.tone ?? 'info')}>{props.children}</div>;
}

// ---------------------------------------------------------------------------
// Krátké oznámení dole na obrazovce, volitelně s tlačítkem „Vrátit“.
// ---------------------------------------------------------------------------

type ToastMsg = { text: string; action?: { label: string; run: () => void } };
let showToastImpl: (m: ToastMsg) => void = () => {};
export const toast = (text: string, action?: ToastMsg['action']) => showToastImpl({ text, action });

export function ToastHost() {
  const [msg, setMsg] = useState<ToastMsg | null>(null);
  useEffect(() => {
    let timer: number | undefined;
    showToastImpl = (m) => {
      setMsg(m);
      clearTimeout(timer);
      timer = window.setTimeout(() => setMsg(null), m.action ? 6000 : 2500);
    };
    return () => clearTimeout(timer);
  }, []);
  if (!msg) return null;
  return (
    <div className="toast" role="status">
      <span>{msg.text}</span>
      {msg.action && (
        <button
          onClick={() => {
            msg.action!.run();
            setMsg(null);
          }}
        >
          {msg.action.label}
        </button>
      )}
    </div>
  );
}

/** Spodní panel s volbami. */
export function Sheet(props: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  if (!props.open) return null;
  return (
    <div className="sheet-backdrop" onClick={props.onClose}>
      <div className="sheet" role="dialog" aria-label={props.title} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <h2>{props.title}</h2>
        {props.children}
      </div>
    </div>
  );
}

/** Text chyby pro uživatele (bez technické předpony „Error:“). */
export const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function SaveBar(props: { onSave: () => void | Promise<unknown>; label?: string; disabled?: boolean; extra?: ReactNode }) {
  // během ukládání je tlačítko zablokované – dvojí ťuknutí neuloží zápis dvakrát
  const [busy, setBusy] = useState(false);
  return (
    <div className="savebar">
      {props.extra}
      <button
        className="btn primary big"
        disabled={props.disabled || busy}
        onClick={async () => {
          if (busy) return;
          setBusy(true);
          try {
            await props.onSave();
          } catch (e) {
            toast(errorText(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {props.label ?? 'Uložit'}
      </button>
    </div>
  );
}
